import { yupResolver } from '@hookform/resolvers/yup';
import { useTheme, useMediaQuery, } from '@mui/material';
import { defaults } from 'chart.js';
import { Chart as ChartJS, CategoryScale } from 'chart.js/auto';
import dayjs from 'dayjs';
import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';

import { FormValidationDateInterval } from '../forms/formValidations';
import { MobileDashboardElements } from './MobileDashboardElements';
import {
    acGetScoreMetrics,
    acGetScoreMetricsByDate,
    acCleanUsageLogMetrics
} from '../../store/actions/ActionCompanyUsageLog';
import { CHART_COLORS } from '../../store/constants/ChartColors';

import DashboardFilter from './DashboardFilter'
import DashboardStats from "./DashboardStats"
import DashboardDistribution from './DashboardDistribution';
import DashboardTrends from './DashboardTrends';
import { Stack } from '@mui/system';


const defaultValues = {
    periodStart: null,
    periodEnd: null,
    cnpjSearch: '',
}

/**
 * Renders the dashboard elements with real data from Redux store.
 *
 * @author Isadora Botassari, pvborgesz
 * @date 2025-06-17
 * @returns {JSX.Element} The rendered dashboard elements with real data.
 */
export const DashboardElements = () => {
    // Hooks
    const dispatch = useDispatch();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'));

    const enableMobile = false // TO-DO: remover isso depois de resolver as questões do código

    const todayDate = dayjs();
    defaults.color = theme.palette.text.primary;

    // Redux state selectors
    const usageLogState = useSelector(state => state.usageLog) || {};

    const scoreMetrics = {
        data: usageLogState.scoreMetricsData,
        loading: usageLogState.isLoadingScoreMetrics || false,
        error: usageLogState.hasErrorScoreMetrics,
    };

    const uniqueDocuments = {
        data: usageLogState.uniqueDocumentsData || [],
        loading: usageLogState.isLoadingUniqueDocuments || false,
        error: usageLogState.hasErrorUniqueDocuments,
    };

    const scoreMetricsByDate = {
        data: usageLogState.scoreMetricsByDateData || [],
        loading: usageLogState.isLoadingScoreMetricsByDate || false,
        error: usageLogState.hasErrorScoreMetricsByDate,
    };

    const [currentPeriod, setCurrentPeriod] = useState({
        startDate: todayDate.subtract(1, 'month'),
        endDate: todayDate,
    });

    const [isFiltered, setIsFiltered] = useState(false);
    const [filteredCNPJ, setFilteredCNPJ] = useState('');

    // Form setup
    const { control, watch, handleSubmit, formState: { errors }, register, reset, setValue } = useForm({
        defaultValues: defaultValues,
        resolver: yupResolver(FormValidationDateInterval),
    });

    const currentStartDate = watch('periodStart') || currentPeriod.startDate;
    const currentEndDate = watch('periodEnd') || currentPeriod.endDate;

    // Chart configuration
    ChartJS.register(CategoryScale);

    const getBaseChartOptions = (showLegend = false) => ({
        maintainAspectRatio: false,
        responsive: true,
        scales: {
            x: {
                ticks: {
                    color: theme.palette.text.primary,
                    maxTicksLimit: 15,
                },
                grid: {
                    color: theme.palette.divider,
                    display: false,
                },
            },
            y: {
                ticks: {
                    color: theme.palette.text.primary,
                },
                grid: {
                    color: theme.palette.divider,
                },
                beginAtZero: true,
                ...(showLegend && { max: 1000 }),
            },
        },
        elements: {
            line: {
                tension: 0
            }
        },
        plugins: {
            legend: {
                display: showLegend,
            },
        },
    });

    const getTrendBarChartOptions = () => ({
        maintainAspectRatio: false,
        responsive: true,
        scales: {
            x: {
                ticks: {
                    color: theme.palette.text.primary,
                    maxTicksLimit: 10,
                },
                grid: {
                    display: false,
                },
            },
            y: {
                ticks: {
                    color: theme.palette.text.primary,
                },
                grid: {
                    color: theme.palette.divider,
                },
                beginAtZero: true,
                max: 1000,
            },
        },
        plugins: {
            legend: {
                display: false,
            },
        },
    });

    const lineChartOptions = getBaseChartOptions(false);
    const barChartOptions = getBaseChartOptions(false);
    const trendBarChartOptions = getTrendBarChartOptions();

    useEffect(() => {
        dispatch(acGetScoreMetrics({}));
        dispatch(acGetScoreMetricsByDate({}));

        return () => {
            dispatch(acCleanUsageLogMetrics());
        };
    }, [dispatch]);

    const generateDistributionChart = () => {
        if (!scoreMetrics.data?.distribution_score) {
            return {
                labels: ['0-100', '101-200', '201-300', '301-400', '401-500', '501-600', '601-700', '701-800', '801-900', '901-1000'],
                datasets: [{
                    label: 'Quantidade',
                    data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
                    backgroundColor: theme.palette.primary.main,
                }]
            };
        }

        const distribution = scoreMetrics.data.distribution_score;
        return {
            labels: Object.keys(distribution),
            datasets: [{
                label: 'Quantidade',
                data: Object.values(distribution),
                backgroundColor: theme.palette.primary.main,
            }]
        };
    };

    const generateFilteredScoreData = () => {
        if (!scoreMetricsByDate.data || scoreMetricsByDate.data.length === 0) {
            return {
                labels: [],
                datasets: []
            };
        }

        const dates = scoreMetricsByDate.data.map(item => dayjs(item.date).format('DD/MM'));
        const avgScores = scoreMetricsByDate.data.map(item => {
            const score = typeof item.average_score === 'string'
                ? parseFloat(item.average_score)
                : item.average_score;
            return score || 0;
        });

        return {
            labels: dates,
            datasets: [{
                label: 'Score Médio',
                data: avgScores,
                borderColor: CHART_COLORS.primary,
                backgroundColor: CHART_COLORS.primary + '20',
                fill: true,
                tension: 0,
                pointRadius: 3,
                pointHoverRadius: 5,
            }]
        };

    };

    const generateRealBarChartData = (label, color, dataField) => {

        if (!scoreMetricsByDate.data ||
            !Array.isArray(scoreMetricsByDate.data) ||
            scoreMetricsByDate.data.length === 0) {

            return {
                labels: [],
                datasets: [{
                    label: label,
                    data: [],
                    backgroundColor: color,
                    borderColor: color,
                    borderWidth: 1,
                }]
            };
        }

        const dates = scoreMetricsByDate.data.map(item => {
            const formattedDate = dayjs(item.date).format('DD/MM');
            return formattedDate;
        });

        const values = scoreMetricsByDate.data.map(item => {
            const value = item[dataField] || 0;
            return value;
        });


        return {
            labels: dates,
            datasets: [{
                label: label,
                data: values,
                backgroundColor: color,
                borderColor: color,
                borderWidth: 1,
            }]
        };
    };

    const onFilterSubmit = handleSubmit((data) => {
        const filters = {};

        if (data.periodStart && data.periodEnd) {
            filters.startDate = dayjs(data.periodStart).format('YYYY-MM-DD');
            filters.endDate = dayjs(data.periodEnd).format('YYYY-MM-DD');

            setCurrentPeriod({
                startDate: dayjs(data.periodStart),
                endDate: dayjs(data.periodEnd)
            });
        }

        if (data.cnpjSearch && data.cnpjSearch.trim() !== '') {
            filters.document = data.cnpjSearch.trim();
            setIsFiltered(true);
            setFilteredCNPJ(data.cnpjSearch.trim());
        } else {
            setIsFiltered(false);
            setFilteredCNPJ('');
        }

        dispatch(acGetScoreMetrics(filters));
        dispatch(acGetScoreMetricsByDate(filters));
    });

    const onClearFilter = () => {
        setIsFiltered(false);
        setFilteredCNPJ('');
        reset();

        const defaultPeriod = {
            startDate: todayDate.subtract(1, 'month'),
            endDate: todayDate,
        };

        setCurrentPeriod(defaultPeriod);

        dispatch(acGetScoreMetrics({}));
        dispatch(acGetScoreMetricsByDate({}));
    };

    const currentData = scoreMetrics.data || {
        total_queries: 0,
        avarage_score: 0,
        max_score: 0,
        min_score: 0,
    };

    const scoreDistributionData = isFiltered
        ? generateFilteredScoreData()
        : generateDistributionChart();

    const chartData = {
        averageScoreByDay: generateRealBarChartData('Média do Score', CHART_COLORS.average, 'average_score'),
        maxScoreByDay: generateRealBarChartData('Score Máximo', CHART_COLORS.maximum, 'max_score'),
        minScoreByDay: generateRealBarChartData('Score Mínimo', CHART_COLORS.minimum, 'min_score'),
    };


    if (isMobile && enableMobile) {
        return (
            <MobileDashboardElements
                currentPeriod={currentPeriod}
                onSubmit={onFilterSubmit}
                control={control}
                currentEndDate={currentEndDate}
                currentStartDate={currentStartDate}
                populationDistChart={scoreDistributionData}
                errors={errors}
                dashboardData={currentData}
                averageScoreByDay={chartData.averageScoreByDay}
                maxScoreByDay={chartData.maxScoreByDay}
                minScoreByDay={chartData.minScoreByDay}
                isFiltered={isFiltered}
                filteredCNPJ={filteredCNPJ}
                onClearFilter={onClearFilter}
                loading={scoreMetrics.loading || scoreMetricsByDate.loading}
                error={scoreMetrics.error || scoreMetricsByDate.error}
            />
        );
    }

    return (
        <Stack gap={3}>

            <DashboardFilter
                currentData={currentData}
                filteredCNPJ={filteredCNPJ}
                uniqueDocuments={uniqueDocuments}
                scoreMetrics={scoreMetrics}
                scoreMetricsByDate={scoreMetricsByDate}
                onClearFilter={onClearFilter}
                onFilterSubmit={onFilterSubmit}
                control={control}
                errors={errors}
                currentStartDate={currentStartDate}
                currentEndDate={currentEndDate}
                isFiltered={isFiltered}
                setValue={setValue}
                register={register}
                watch={watch}
            />

            <DashboardStats
                theme={theme}
                isFiltered={isFiltered}
                filteredCNPJ={filteredCNPJ}
                scoreMetrics={scoreMetrics}
                currentData={currentData}
            />

            <DashboardDistribution
                isFiltered={isFiltered}
                filteredCNPJ={filteredCNPJ}
                scoreMetrics={scoreMetrics}
                scoreDistributionData={scoreDistributionData}
                lineChartOptions={lineChartOptions}
                barChartOptions={barChartOptions}
            />

            {!isFiltered && <DashboardTrends
                trendBarChartOptions={trendBarChartOptions}
                chartData={chartData}
                scoreMetricsByDate={scoreMetricsByDate}
            />}
        </Stack>
    )
};

